import axios from 'axios';
import { axiosInstance } from './axiosInstance';
import { endpoint, fwRefusalLabels } from './constants';

export const workflowModes = {
    LOADING: 'loading',
    CONFIGURED: 'configured',
    DISABLED: 'disabled',
    ERROR: 'error',
};

const DISABLED_CODE = 'FW_AUTOMATION_DISABLED';
const REVISION_PATTERN = /^(?:[0-9A-F]{2}){1,16}$/;

/**
 * One module state for every FW page that depends on the workflow backend: a
 * published configuration answers with its limits, a switched-off module
 * answers `disabled`, and anything else — a provisioning failure, a transport
 * error — is an `error` the caller reports rather than mistaking for legacy.
 */
export const isCancelled = error => axios.isCancel(error);

export const probeWorkflow = (config = {}) =>
    axiosInstance
        .get(endpoint.FW_SETTINGS, config)
        .then(response => ({ mode: workflowModes.CONFIGURED, settings: response?.data ?? null }))
        .catch(error => {
            if (isCancelled(error)) return { mode: workflowModes.LOADING, refusal: null };
            const refusal = refusalOf(error);

            return {
                mode: isDisabledRefusal(refusal) ? workflowModes.DISABLED : workflowModes.ERROR,
                refusal,
            };
        });

export const isDisabledRefusal = refusal =>
    refusal?.code === DISABLED_CODE || refusal?.message === DISABLED_CODE;

/**
 * A refusal reduced to what a page needs: the status, the mutation `code` the
 * workflow routes answer, the generic `message` the middleware answers, and the
 * per-field errors of a 422.
 */
export const refusalOf = error => {
    const response = error?.response;
    const data = response?.data;
    const fields = data?.errors && typeof data.errors === 'object' ? data.errors : null;
    const fieldCode = fields
        ? (Object.values(fields)
              .flat()
              .find(value => typeof value === 'string' && value !== '') ?? null)
        : null;

    return {
        status: response?.status ?? null,
        code: data?.code ?? null,
        message: data?.message ?? null,
        fields,
        fieldCode,
    };
};

/**
 * One task's start outcomes from the read model: the payload, or the module
 * state that kept the page from reading it. An unknown task is its own state,
 * because a missing task is not a broken module.
 */
export const readTaskStatus = (taskId, config = {}) =>
    axiosInstance
        .get(`${endpoint.FW_READ_MODEL}/tasks/${taskId}/status`, config)
        .then(response => ({ state: 'read', data: response?.data?.data ?? null }))
        .catch(error => {
            if (isCancelled(error)) return { state: workflowModes.LOADING, data: null };
            const refusal = refusalOf(error);
            if (refusal.status === 404) return { state: 'not_found', data: null, refusal };

            return {
                state: isDisabledRefusal(refusal) ? workflowModes.DISABLED : workflowModes.ERROR,
                data: null,
                refusal,
            };
        });

/**
 * The operator-facing text for a refusal. A field code wins over the generic
 * `FW_INVALID_INPUT`, because the field is what the operator has to fix.
 */
export const refusalText = (refusal, fallback) => {
    const candidates = [refusal?.fieldCode, refusal?.code, refusal?.message];

    for (const candidate of candidates) {
        if (!candidate || candidate === 'FW_INVALID_INPUT' || candidate === 'FW_OPERATION_RECORDED') continue;

        return fwRefusalLabels[candidate] ?? candidate;
    }

    return fallback;
};

/**
 * The field errors of a 422, keyed by field name, each reduced to its first
 * code's label so a form can show it beside the field it names.
 */
export const fieldErrors = refusal => {
    if (!refusal?.fields) return {};

    return Object.entries(refusal.fields).reduce((collected, [field, messages]) => {
        const code = Array.isArray(messages) ? messages[0] : messages;
        if (code) collected[field] = fwRefusalLabels[code] ?? String(code);
        return collected;
    }, {});
};

export const operationKey = () => {
    if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();

    return `fw-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

export const normalizeRevision = value =>
    String(value ?? '')
        .trim()
        .toUpperCase();

export const isValidRevision = value => REVISION_PATTERN.test(normalizeRevision(value));

export const isValidLimit = (value, field) =>
    Number.isInteger(value) && value >= field.min && value <= field.max;

/**
 * Whether a chosen file may be uploaded as an artifact of the given family: the
 * name ends `.bin`, starts with the family, carries no control characters and
 * stays within the server's length bound.
 */
const hasControlCharacter = value =>
    Array.from(value).some(character => {
        const code = character.charCodeAt(0);

        return code < 0x20 || code === 0x7f;
    });

export const isValidArtifactFile = (filename, family) => {
    const name = String(filename ?? '').trim();
    const artifactFamily = String(family ?? '')
        .trim()
        .toUpperCase();
    if (name.length < 1 || name.length > 255) return false;
    if (hasControlCharacter(name)) return false;
    if (!name.toLowerCase().endsWith('.bin')) return false;

    return name.toUpperCase().startsWith(artifactFamily) && artifactFamily !== '';
};

export const shortDigest = value => {
    const digest = String(value ?? '');

    return digest.length > 12 ? `${digest.slice(0, 12)}…` : digest;
};
